<template>
  <div>
  <HeaderComponent/>
  <main class="pt-[70px] w-full h-screen bg-gray-100 relative flex">
      <SideBarComponent/>
      <div class="lg:w-[calc(100%-250px)] md:w-[calc(100%-78px)] lg:left-[250px] md:left-[78px] relative top-0 overflow-y-auto h-full md:p-[25px] p-[20px]">
          <section class="w-full relative flex flex-wrap items-center justify-between mb-[40px]">
              <div class="w-full">
                <div class="">
                  <!-- Header -->
                  <div class="flex items-center justify-between mb-8">
                    <h1 class="text-2xl font-bold text-gray-800">Modifier l'appartement</h1>
                    <button 
                      @click="router.push('/admin/apartments')"
                      class="flex items-center gap-2 text-gray-600 hover:text-[#D4AF37] transition-colors"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fill-rule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clip-rule="evenodd" />
                      </svg>
                      Retour à la liste
                    </button>
                  </div>
                
                  <!-- Formulaire -->
                  <form @submit.prevent="showCategoryModal = true" novalidate class="bg-white rounded-xl shadow-md overflow-hidden">
                    <!-- Sections organisées en onglets -->
                    <div class="border-b border-gray-200">
                      <nav class="flex -mb-px">
                        <div
                          v-for="tab in tabs"
                          :key="tab.id"
                          @click="activeTab = tab.id"
                          :class="{
                            'border-[#D4AF37] text-[#D4AF37]': activeTab === tab.id,
                            'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300': activeTab !== tab.id
                          }"
                          class="whitespace-nowrap cursor-pointer py-4 px-6 border-b-2 font-medium text-sm"
                        >
                          {{ tab.label }}
                        </div>
                      </nav>
                    </div>
                  
                    <!-- Section Informations de base -->
                    <div v-show="activeTab === 'info'" class="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                      <!-- Colonne gauche -->
                      <div class="space-y-6">
                        <div>
                          <label class="block text-sm font-medium text-gray-700 mb-1">Titre*</label>
                          <input
                            v-model="form.title"
                            required
                            placeholder="Ex: Bel appartement centre-ville"
                            class="w-full p-3 border border-gray-300 outline-none rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-[#D4AF37] transition"
                          />
                        </div>
                      
                        <div>
                          <label class="block text-sm font-medium text-gray-700 mb-1">Description*</label>
                          <textarea
                            v-model="form.description"
                            required
                            rows="4"
                            placeholder="Décrivez l'appartement en détail..."
                            class="w-full p-3 border border-gray-300 outline-none rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-[#D4AF37] transition"
                          ></textarea>
                        </div>
                      
                        <div>
                          <label class="block text-sm font-medium text-gray-700 mb-1">Type*</label>
                          <select
                            v-model="form.type"
                            required
                            class="w-full p-3 border border-gray-300 outline-none h-[50px] rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-[#D4AF37] transition"
                          >
                            <option value="">Sélectionnez un type</option>
                            <option value="Appartement">Appartement</option>
                            <option value="Studio">Studio</option>
                            <option value="Maison">Maison</option>
                            <option value="Villa">Villa</option>
                          </select>
                        </div>
                      
                        <div class="grid grid-cols-2 gap-4">
                          <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">Pays*</label>
                            <select
                              v-model="form.country"
                              required
                              class="w-full p-3 border border-gray-300 outline-none rounded-lg h-[50px] focus:ring-2 focus:ring-[#D4AF37] focus:border-[#D4AF37] transition appearance-none"
                            >
                              <option value="" disabled selected>Sélectionnez un pays</option>
                              <option value="Bénin">Bénin</option>
                              <option value="Togo">Togo</option>
                              <option value="Nigéria">Nigéria</option>
                              <option value="Côte d'Ivoire">Côte d'Ivoire</option>
                              <option value="France">France</option>
                              <option value="Ghana">Ghana</option>
                            </select>
                          </div>
                          <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">Ville*</label>
                            <input
                              v-model="form.locality"
                              required
                              placeholder="Ex: Cotonou"
                              class="w-full p-3 border border-gray-300 outline-none rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-[#D4AF37] transition"
                            />
                          </div>
                        </div>

                        <div v-if="authStore.canViewOwnerInfo" class="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">Prix propriétaire — Nuitée (FCFA)</label>
                            <div class="relative">
                              <input
                                v-model.number="form.ownerPriceNight"
                                type="number"
                                min="0"
                                placeholder="Ex: 13000"
                                class="w-full p-3 pl-[50px] border border-gray-300 outline-none rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-[#D4AF37] transition"
                              />
                              <span class="absolute left-3 top-3 text-gray-500 text-sm">FCFA</span>
                            </div>
                          </div>
                          <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">Prix propriétaire — Mois (FCFA)</label>
                            <div class="relative">
                              <input
                                v-model.number="form.ownerPriceMonth"
                                type="number"
                                min="0"
                                placeholder="Ex: 650000"
                                class="w-full p-3 pl-[50px] border border-gray-300 outline-none rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-[#D4AF37] transition"
                              />
                              <span class="absolute left-3 top-3 text-gray-500 text-sm">FCFA</span>
                            </div>
                          </div>
                          <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">Numéro du propriétaire</label>
                            <input
                              v-model="form.ownerPhone"
                              placeholder="Ex: +229 01 90 00 00 00"
                              class="w-full p-3 border border-gray-300 outline-none rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-[#D4AF37] transition"
                            />
                          </div>
                        </div>
                        <div v-else class="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
                          Les informations propriétaire sont réservées au service client et aux super admins.
                        </div>
                      </div>
                    
                      <!-- Colonne droite -->
                      <div class="space-y-6">
                        <div class="grid grid-cols-2 gap-4">
                          <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">Prix (FCFA)*</label>
                            <div class="relative">
                              <input
                                v-model.number="form.price"
                                type="number"
                                min="0"
                                required
                                class="w-full p-3 border border-gray-300 outline-none rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-[#D4AF37] transition pl-[50px]"
                              />
                              <span class="absolute left-3 top-3 text-gray-500">FCFA</span>
                            </div>
                          </div>
                          <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">Chambres*</label>
                            <input
                              v-model.number="form.rooms"
                              type="number"
                              min="1"
                              required
                              class="w-full p-3 border border-gray-300 outline-none rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-[#D4AF37] transition"
                            />
                          </div>
                        </div>
                      
                        <div class="grid grid-cols-3 gap-4">
                          <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">Type de lit</label>
                            <input
                              v-model="form.bedType"
                              placeholder="Ex: Double"
                              class="w-full p-3 border border-gray-300 outline-none rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-[#D4AF37] transition"
                            />
                          </div>
                          <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">Étage</label>
                            <select
                              v-model="form.floor"
                              class="w-full p-3 border border-gray-300 h-[50px] outline-none rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-[#D4AF37] transition"
                            >
                              <option disabled value="">Sélectionnez un étage</option>
                              <option value="RDC">RDC</option>
                              <option value="1er">1er</option>
                              <option value="2ème">2ème</option>
                              <option value="3ème">3ème</option>
                              <option value="4ème">4ème</option>
                              <option value="5ème et +">5ème et +</option>
                            </select>
                          </div>
                          <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">Disponibilité*</label>
                            <select
                              v-model="form.availability"
                              required
                              class="w-full p-3 border border-gray-300 outline-none h-[50px] rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-[#D4AF37] transition"
                            >
                              <option value="available">Disponible</option>
                              <option value="unavailable">Indisponible</option>
                            </select>
                          </div>
                        </div>
                      
                        <div v-if="form.availability === 'unavailable'">
                          <label class="block text-sm font-medium text-gray-700 mb-1">Date de disponibilité</label>
                          <div class="relative">
                            <input
                              v-model="form.unavailabilityEndDate"
                              type="date"
                              class="w-full p-3 border border-gray-300 outline-none rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-[#D4AF37] transition"
                            />
                          </div>
                        </div>

                        <div>
                          <label class="block text-sm font-medium text-gray-700 mb-1">Prix par semaine (FCFA)</label>
                          <div class="relative">
                            <input
                              v-model.number="form.weeklyPrice"
                              type="number"
                              min="0"
                              class="w-full p-3 border border-gray-300 outline-none rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-[#D4AF37] transition pl-[50px]"
                            />
                            <span class="absolute left-3 top-3 text-gray-500">FCFA</span>
                          </div>
                        </div>

                        <div>
                          <label class="block text-sm font-medium text-gray-700 mb-1">Prix du mois (FCFA)</label>
                          <div class="relative">
                            <input
                              v-model.number="form.longStayPrice"
                              type="number"
                              min="0"
                              class="w-full p-3 border border-gray-300 outline-none rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-[#D4AF37] transition pl-[50px]"
                            />
                            <span class="absolute left-3 top-3 text-gray-500">FCFA</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  
                    <!-- Section Équipements -->
                    <div v-show="activeTab === 'equipment'" class="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <h3 class="text-lg font-medium text-gray-900 mb-4">Équipements principaux</h3>
                        <div class="space-y-3">
                          <label class="flex items-center space-x-3">
                            <input type="checkbox" v-model="form.wifi" class="h-5 w-5 text-[#D4AF37] rounded border-gray-300 focus:ring-[#D4AF37]" />
                            <span class="text-gray-700">Wi-Fi</span>
                          </label>
                          <label class="flex items-center space-x-3">
                            <input type="checkbox" v-model="form.security" class="h-5 w-5 text-[#D4AF37] rounded border-gray-300 focus:ring-[#D4AF37]" />
                            <span class="text-gray-700">Système de sécurité</span>
                          </label>
                          <label class="flex items-center space-x-3">
                            <input type="checkbox" v-model="form.waterHeater" class="h-5 w-5 text-[#D4AF37] rounded border-gray-300 focus:ring-[#D4AF37]" />
                            <span class="text-gray-700">Cuisine intégrée</span>
                          </label>
                          <label class="flex items-center space-x-3">
                            <input type="checkbox" v-model="form.terrace" class="h-5 w-5 text-[#D4AF37] rounded border-gray-300 focus:ring-[#D4AF37]" />
                            <span class="text-gray-700">Terrasse/Balcon</span>
                          </label>
                          <label class="flex items-center space-x-3">
                            <input type="checkbox" v-model="form.privateEntrance" class="h-5 w-5 text-[#D4AF37] rounded border-gray-300 focus:ring-[#D4AF37]" />
                            <span class="text-gray-700">Entrée privée</span>
                          </label>
                          <label class="flex items-center space-x-3">
                            <input type="checkbox" v-model="form.smokerFriendly" class="h-5 w-5 text-[#D4AF37] rounded border-gray-300 focus:ring-[#D4AF37]" />
                            <span class="text-gray-700">Fumeurs autorisés</span>
                          </label>
                          <label class="flex items-center space-x-3">
                            <input type="checkbox" v-model="form.jardin" class="h-5 w-5 text-[#D4AF37] rounded border-gray-300 focus:ring-[#D4AF37]" />
                            <span class="text-gray-700">Avec jardin</span>
                          </label>
                          <label class="flex items-center space-x-3">
                            <input type="checkbox" v-model="form.piscine" class="h-5 w-5 text-[#D4AF37] rounded border-gray-300 focus:ring-[#D4AF37]" />
                            <span class="text-gray-700">Avec piscine</span>
                          </label>
                          <label class="flex items-center space-x-3">
                            <input type="checkbox" v-model="form.tv" class="h-5 w-5 text-[#D4AF37] rounded border-gray-300 focus:ring-[#D4AF37]" />
                            <span class="text-gray-700">Téléviseur</span>
                          </label>
                          <label class="flex items-center space-x-3">
                            <input type="checkbox" v-model="form.generator" class="h-5 w-5 text-[#D4AF37] rounded border-gray-300 focus:ring-[#D4AF37]" />
                            <span class="text-gray-700">Groupe électrogène</span>
                          </label>
                          <label class="flex items-center space-x-3">
                            <input type="checkbox" v-model="form.washingMachine" class="h-5 w-5 text-[#D4AF37] rounded border-gray-300 focus:ring-[#D4AF37]" />
                            <span class="text-gray-700">Machine à laver</span>
                          </label>
                          <label class="flex items-center space-x-3">
                            <input type="checkbox" v-model="form.electricityClientCharge" class="h-5 w-5 text-[#D4AF37] rounded border-gray-300 focus:ring-[#D4AF37]" />
                            <span class="text-gray-700">Électricité à la charge du client</span>
                          </label>
                          <label class="flex items-center space-x-3">
                            <input type="checkbox" v-model="form.iron" class="h-5 w-5 text-[#D4AF37] rounded border-gray-300 focus:ring-[#D4AF37]" />
                            <span class="text-gray-700">Fer à repasser</span>
                          </label>
                          <label class="flex items-center space-x-3">
                            <input type="checkbox" v-model="form.airConditioner" class="h-5 w-5 text-[#D4AF37] rounded border-gray-300 focus:ring-[#D4AF37]" />
                            <span class="text-gray-700">Climatiseur</span>
                          </label>
                          <label class="flex items-center space-x-3">
                            <input type="checkbox" v-model="form.fan" class="h-5 w-5 text-[#D4AF37] rounded border-gray-300 focus:ring-[#D4AF37]" />
                            <span class="text-gray-700">Ventilé</span>
                          </label>
                          <label class="flex items-center space-x-3">
                            <input type="checkbox" v-model="form.partiesAllowed" class="h-5 w-5 text-[#D4AF37] rounded border-gray-300 focus:ring-[#D4AF37]" />
                            <span class="text-gray-700">Fêtes autorisées</span>
                          </label>
                        </div>
                      </div>
                    
                      <div>
                        <h3 class="text-lg font-medium text-gray-900 mb-4">Équipements supplémentaires</h3>
                        <div class="space-y-3">
                          <label class="flex items-center space-x-3">
                            <input type="checkbox" v-model="form.extra.microwave" class="h-5 w-5 text-[#D4AF37] rounded border-gray-300 focus:ring-[#D4AF37]" />
                            <span class="text-gray-700">Micro-ondes</span>
                          </label>
                          <label class="flex items-center space-x-3">
                            <input type="checkbox" v-model="form.extra.canal" class="h-5 w-5 text-[#D4AF37] rounded border-gray-300 focus:ring-[#D4AF37]" />
                            <span class="text-gray-700">Canal+</span>
                          </label>
                          <label class="flex items-center space-x-3">
                            <input type="checkbox" v-model="form.extra.netflix" class="h-5 w-5 text-[#D4AF37] rounded border-gray-300 focus:ring-[#D4AF37]" />
                            <span class="text-gray-700">Netflix</span>
                          </label>
                        </div>
                      </div>
                    </div>
                  
                    <!-- Section Médias -->
                    <div v-show="activeTab === 'media'" class="p-6 space-y-6">
                      <!-- Images existantes -->
                      <div v-if="form.images?.length">
                        <h3 class="text-lg font-medium text-gray-900 mb-4">Images actuelles</h3>
                        <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                          <div v-for="(img, index) in form.images" :key="index" class="relative group">
                            <img
                              :src="'https://api.mohimmo.com'+getImageUrl(img)"
                              class="w-full h-40 object-cover rounded-lg border border-gray-200"
                            />
                            <button
                              type="button"
                              @click="downloadExistingImage(img)"
                              class="absolute top-2 left-2 bg-black/60 hover:bg-black/80 text-white p-1.5 rounded-full md:opacity-0 md:group-hover:opacity-100 transition-opacity"
                              title="Télécharger l'image"
                              aria-label="Télécharger l'image"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M5 20h14v-2H5zM19 9h-4V3H9v6H5l7 7z"/>
                              </svg>
                            </button>
                            <div
                              @click="removeImage(index)"
                              class="absolute cursor-pointer top-2 right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                              </svg>
                            </div>
                          </div>
                        </div>
                      </div>
                    
                      <!-- Ajout d'images -->
                      <div>
                        <h3 class="text-lg font-medium text-gray-900 mb-4">Ajouter des images</h3>
                        <div class="flex items-center justify-center w-full">
                          <label class="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition">
                            <div class="flex flex-col items-center justify-center pt-5 pb-6">
                              <svg class="w-8 h-8 mb-4 text-gray-500" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 16">
                                <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"/>
                              </svg>
                              <p class="mb-2 text-sm text-gray-500">
                                <span class="font-semibold">Cliquez pour uploader</span> ou glissez-déposez
                              </p>
                              <p class="text-xs text-gray-500">JPEG, PNG</p>
                            </div>
                            <input 
                              type="file" 
                              multiple 
                              accept="image/*" 
                              @change="handleImages" 
                              class="hidden"
                            />
                          </label>
                        </div>
                        <div v-if="newImages.length" class="mt-4">
                          <p class="text-sm font-medium text-gray-700 mb-2">Nouvelles images sélectionnées:</p>
                          <div class="flex flex-wrap gap-2">
                            <div v-for="(img, index) in newImages" :key="index" class="relative">
                              <img 
                                :src="getImagePreview(img)" 
                                class="w-20 h-20 object-cover rounded border border-gray-200"
                              />
                              <div
                                @click="removeNewImage(index)"
                                class="absolute cursor-pointer -top-2 -right-2 bg-red-500 text-white p-1 rounded-full text-xs"
                              >
                                ×
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    
                      <!-- Vidéo existante -->
                      <div v-if="form.video">
                        <h3 class="text-lg font-medium text-gray-900 mb-4">Vidéo actuelle</h3>
                        <div class="relative max-w-md">
                          <video 
                            :src="'https://api.mohimmo.com'+getVideoUrl(form.video)" 
                            controls
                            class="w-full rounded-lg border border-gray-200"
                          ></video>
                          <div
                            @click="removeVideo()"
                            class="absolute cursor-pointer top-2 right-2 bg-red-500 text-white p-1 rounded-full"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    
                      <!-- Ajout de vidéo -->
                      <div>
                        <h3 class="text-lg font-medium text-gray-900 mb-4">Remplacer la vidéo</h3>
                        <div class="flex items-center justify-center w-full">
                          <label class="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition">
                            <div class="flex flex-col items-center justify-center pt-5 pb-6">
                              <svg class="w-8 h-8 mb-4 text-gray-500" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 16">
                                <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"/>
                              </svg>
                              <p class="mb-2 text-sm text-gray-500">
                                <span class="font-semibold">Cliquez pour uploader</span> une vidéo
                              </p>
                              <p class="text-xs text-gray-500">MP4, MOV</p>
                            </div>
                            <input 
                              type="file" 
                              accept="video/*" 
                              @change="handleVideo" 
                              class="hidden"
                            />
                          </label>
                        </div>
                        <div v-if="newVideo" class="mt-4">
                          <p class="text-sm font-medium text-gray-700 mb-2">Nouvelle vidéo sélectionnée:</p>
                          <div class="flex items-center gap-2">
                            <span class="text-sm text-gray-500">{{ newVideo.name }}</span>
                            <div
                              @click="removeNewVideo()"
                              class="text-red-500 text-sm cursor-pointer"
                            >
                              Annuler
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  
                    <!-- Boutons de navigation -->
                    <div class="border-t border-gray-200 px-6 py-4 flex justify-between">
                      <button
                        v-if="activeTab !== 'info'"
                        @click="activeTab = tabs[tabs.findIndex(t => t.id === activeTab)-1].id"
                        type="button"
                        class="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#D4AF37]"
                      >
                        Précédent
                      </button>
                      <div v-else></div>
                    
                      <button
                        v-if="activeTab !== 'media'"
                        @click="activeTab = tabs[tabs.findIndex(t => t.id === activeTab)+1].id"
                        type="button"
                        class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#D4AF37] hover:bg-[#C9A227] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#D4AF37]"
                      >
                        Suivant
                      </button>
                      <button
                        v-else
                        type="button"
                        @click="showCategoryModal = true"
                        class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#D4AF37] hover:bg-[#C9A227] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#D4AF37]"
                      >
                        Enregistrer les modifications
                      </button>
                    </div>
                  </form>
                </div>
              </div>
          </section>
      </div>
  </main>

  <!-- Modal de sélection de catégorie -->
  <div v-if="showCategoryModal" class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
    <div class="bg-white rounded-xl max-w-md w-full p-6">
      <h2 class="text-xl font-bold text-gray-800 mb-4">Choisir la catégorie</h2>
      <p class="text-gray-600 mb-6">Sélectionnez le type de transaction pour ce bien :</p>
      
      <div class="grid grid-cols-2 gap-4 mb-6">
        <div 
          @click="form.category = 'reservation'"
          :class="{
            'border-2 border-[#D4AF37] bg-amber-50': form.category === 'reservation',
            'border border-gray-300 bg-white': form.category !== 'reservation'
          }"
          class="p-4 rounded-lg cursor-pointer transition-all hover:shadow-md"
        >
          <div class="flex flex-col items-center text-center">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12 text-[#D4AF37] mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <h3 class="font-semibold text-gray-800">Réservation</h3>
            <p class="text-sm text-gray-600 mt-1">Location à court ou long terme</p>
          </div>
        </div>
        
        <div 
          @click="form.category = 'achat'"
          :class="{
            'border-2 border-[#D4AF37] bg-amber-50': form.category === 'achat',
            'border border-gray-300 bg-white': form.category !== 'achat'
          }"
          class="p-4 rounded-lg cursor-pointer transition-all hover:shadow-md"
        >
          <div class="flex flex-col items-center text-center">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12 text-[#D4AF37] mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 class="font-semibold text-gray-800">Achat</h3>
            <p class="text-sm text-gray-600 mt-1">Vente définitive du bien</p>
          </div>
        </div>
      </div>

      <div class="flex items-center justify-between">
        <button
          @click="showCategoryModal = false"
          type="button"
          class="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
        >
          Retour
        </button>
        <button
          @click="submitForm"
          class="px-4 py-2 bg-[#D4AF37] text-white rounded-lg hover:bg-[#C9A227] transition"
        >
          Confirmer et enregistrer
        </button>
      </div>
      
      <p class="text-xs text-gray-500 mt-4 text-center">
        Si aucune sélection n'est faite, la catégorie "Réservation" sera utilisée par défaut
      </p>
    </div>
  </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue'
import HeaderComponent from '@/components/HeaderComponentAdmin.vue'
import SideBarComponent from '@/components/SideBarComponent.vue'
import { useRoute, useRouter } from 'vue-router'
import apiClient from '@/services/api'
import { downloadImage } from '@/services/downloadImage'
import { toast } from 'vue3-toastify'
import { useAuthStore } from '@/store/auth'

const route = useRoute()
const router = useRouter()
const apartmentId = route.params.id
const authStore = useAuthStore()

const showCategoryModal = ref(false)

// Onglets du formulaire
const tabs = [
  { id: 'info', label: 'Informations' },
  { id: 'equipment', label: 'Équipements' },
  { id: 'media', label: 'Médias' }
]
const activeTab = ref('info')

// Données du formulaire
const form = ref({
  title: '',
  description: '',
  unavailabilityEndDate: '',
  price: 0,
  shortStayPrice: 0,
  weeklyPrice: 0,
  longStayPrice: 0,
  rooms: 1,
  wifi: false,
  security: false,
  waterHeater: false,
  terrace: false,
  privateEntrance: false,
  smokerFriendly: false,
  jardin: false,
  piscine: false,
  tv: false,
  generator: false,
  washingMachine: false,
  electricityClientCharge: false,
  iron: false,
  airConditioner: false,
  fan: false,
  partiesAllowed: false,
  extra: {
    microwave: false,
    canal: false,
    netflix: false
  },
  bedType: '',
  floor: '',
  country: '',
  locality: '',
  ownerPriceNight: null,
  ownerPriceMonth: null,
  ownerPhone: '',
  type: '',
  availability: 'available',
  category: 'reservation',
  images: [],
  video: ''
})

watch(() => form.value.price, (newPrice) => {
  form.value.shortStayPrice = newPrice;
});

const newImages = ref([])
const newVideo = ref(null)
const MAX_APARTMENT_IMAGES = 30

// Gestion des médias
const handleImages = (e) => {
  const files = Array.from(e.target.files)
  const currentImagesCount = form.value.images?.length || 0
  const totalImagesCount = currentImagesCount + newImages.value.length + files.length

  if (totalImagesCount > MAX_APARTMENT_IMAGES) {
    toast.error(`Vous pouvez conserver jusqu'à ${MAX_APARTMENT_IMAGES} images par logement.`, {
      position: 'top-right',
      autoClose: 3000
    })
    e.target.value = ''
    return
  }

  newImages.value = [...newImages.value, ...files]
  e.target.value = ''
}

const handleVideo = (e) => {
  newVideo.value = e.target.files[0]
}

const removeImage = (index) => {
  form.value.images.splice(index, 1)
}

const removeNewImage = (index) => {
  newImages.value.splice(index, 1)
}

const removeVideo = () => {
  form.value.video = ''
}

const removeNewVideo = () => {
  newVideo.value = null
}

const getImageUrl = (filename) => {
  return filename.startsWith('http') ? filename : `/uploads/${filename}`
}

const downloadExistingImage = (filename) => {
  if (!filename) return
  const url = filename.startsWith('http') ? filename : `https://api.mohimmo.com/uploads/${filename}`
  downloadImage(url, filename)
}

const getVideoUrl = (filename) => {
  return filename.startsWith('http') ? filename : `/uploads/${filename}`
}

const getImagePreview = (file) => {
  if (typeof window !== 'undefined' && window.URL && window.URL.createObjectURL) {
    return URL.createObjectURL(file)
  }
  return ''
}

// Chargement des données initiales
onMounted(async () => {
  try {
    const { data } = await apiClient.get(`/apartments/admin/${apartmentId}`)
    form.value = {
      ...data,
      category: data.category || 'reservation',
      extra: {
        microwave: data.extra?.microwave || false,
        canal: data.extra?.canal || false,
        netflix: data.extra?.netflix || false
      }
    }
  } catch (err) {
    toast.error('Erreur lors du chargement des données.', {
      position: 'top-right',
      autoClose: 3000
    });
  }
})

// Soumission du formulaire
const submitForm = async () => {
  const f = form.value
  if (!f.title?.trim()) {
    toast.error('Le titre est obligatoire.', { position: 'top-right', autoClose: 3000 })
    activeTab.value = 'info'
    return
  }
  if (!f.description?.trim()) {
    toast.error('La description est obligatoire.', { position: 'top-right', autoClose: 3000 })
    activeTab.value = 'info'
    return
  }
  if (!f.type) {
    toast.error('Le type de bien est obligatoire.', { position: 'top-right', autoClose: 3000 })
    activeTab.value = 'info'
    return
  }
  if (!f.country) {
    toast.error('Le pays est obligatoire.', { position: 'top-right', autoClose: 3000 })
    activeTab.value = 'info'
    return
  }
  if (!f.locality?.trim()) {
    toast.error('La ville est obligatoire.', { position: 'top-right', autoClose: 3000 })
    activeTab.value = 'info'
    return
  }
  if (!f.price || f.price <= 0) {
    toast.error('Le prix est obligatoire.', { position: 'top-right', autoClose: 3000 })
    activeTab.value = 'info'
    return
  }
  if (!f.rooms || f.rooms < 1) {
    toast.error('Le nombre de chambres est obligatoire.', { position: 'top-right', autoClose: 3000 })
    activeTab.value = 'info'
    return
  }

  // S'assurer que la catégorie a une valeur (réservation par défaut)
  if (!form.value.category) {
    form.value.category = 'reservation'
  }

  const formData = new FormData()
  const OPTIONAL_NUMBER_FIELDS = ['weeklyPrice', 'longStayPrice', 'shortStayPrice', 'ownerPriceNight', 'ownerPriceMonth']

  // Ajout des données de formulaire
  for (const key in form.value) {
    if (key === 'extra') {
      for (const subKey in form.value.extra) {
        formData.append(`extra[${subKey}]`, form.value.extra[subKey])
      }
    } else if (key === 'images') {
      // Envoyer explicitement le tableau d'images restantes
      formData.append('remainingImages', JSON.stringify(form.value.images))
    } else if ((key === 'ownerPriceNight' || key === 'ownerPriceMonth' || key === 'ownerPhone') && !authStore.canViewOwnerInfo) {
      continue
    } else if (OPTIONAL_NUMBER_FIELDS.includes(key)) {
      const value = form.value[key]
      if (value === null || value === undefined || value === '') continue
      formData.append(key, value)
    } else if (key !== 'video') {
      formData.append(key, form.value[key])
    }
  }

  // Fichiers
  if (newImages.value.length) {
    for (const img of newImages.value) {
      formData.append('images', img)
    }
  }
  if (newVideo.value) {
    formData.append('video', newVideo.value)
  }

  try {
    await apiClient.put(`/apartments/${apartmentId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    toast.success('Appartement modifié avec succès!', {
      position: 'top-right',
      autoClose: 3000
    })
    router.push('/admin/apartments')
  } catch (err) {
    toast.error('Une erreur est survenue lors de la mise à jour. Veuillez réessayer.', {
      position: 'top-right',
      autoClose: 3000
    });
  } finally {
    showCategoryModal.value = false
  }
}
</script>

<style scoped>
/* Styles spécifiques si nécessaire */
</style>
